import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createMemberOption, createTeamOption } from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCanAssignProjectRoles } from "@hooks/feature-control/use-can-assign-project-roles";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useTeamUtils } from "@hooks/use-team-utils";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { UtilsTypes } from "@stellar/utils";
import { setOne } from "@store/members/members-slice";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { projectMembersSelector } from "@store/projects/projects-selector";
import { addProcessingProjects, removeProcessingProjects, setOneProjects } from "@store/projects/projects-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { sortTeams } from "@utils/team-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";
/**
 * Encapsulates the shared logic for inviting members and teams to a project:
 * building the autocomplete options, wiring the members autocomplete, deriving
 * the disabled state and submitting the invitation. Consumers only render the
 * surrounding UI (dialog, inline form, etc.).
 */
export function useInviteMembersToProject({
  project,
  initialRole,
  onInviteSuccess,
  location
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    isTeamName
  } = useTeamUtils();
  const {
    trackEvent
  } = useTrackEvent();
  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(assignableTeamsSelector);
  const {
    respondToUser
  } = useRespondToInvite();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const projectMembers = useAppSelector(projectMembersSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [pickedRole, setPickedRole] = useState(initialRole);
  const canAssignProjectRoles = useCanAssignProjectRoles();

  /** Users without the feature may only invite viewers, so no role is picked. */
  const selectedRole = canAssignProjectRoles ? pickedRole : "RESOURCE_VIEWER";
  const projectMemberMap = useMemo(() => new Map(projectMembers.map(member => [member.identity, member])), [projectMembers]);
  const companyMemberIdentities = useMemo(() => new Set(companyMembers.map(companyMember => companyMember.identity)), [companyMembers]);

  /** Reforming company members data for stellar autocomplete component */
  const memberOptions = useMemo(() => sortMembersByNameOrEmail(companyMembers).map(member_0 => {
    const alreadyInProject = projectMemberMap.get(member_0.identity);
    return createMemberOption({
      member: member_0,
      isDisabled: !!alreadyInProject,
      disabledMessage: alreadyInProject ? `Already in project as ${UtilsTypes.RoleDisplayNames[alreadyInProject.role]}` : undefined
    });
  }), [companyMembers, projectMemberMap]);

  /** Reforming team data for stellar autocomplete component */
  const teamOptions = useMemo(() => sortTeams(teams, "name_ascending").map(team => {
    const alreadyInProject_0 = projectMemberMap.get(team.id);
    return createTeamOption({
      team,
      isDisabled: !!alreadyInProject_0,
      disabledMessage: alreadyInProject_0 ? `Already in project as ${UtilsTypes.RoleDisplayNames[alreadyInProject_0.role]}` : undefined
    });
  }), [projectMemberMap, teams]);
  const autocompleteOptions = useMemo(() => [...teamOptions, ...memberOptions], [teamOptions, memberOptions]);
  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset
  } = useMembersAutocomplete({
    entityName: "project",
    existingMembers: projectMembers,
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map(member_1 => member_1.identity)
  });

  /** Disable the `Send Invite` action if no role or no valid member is selected */
  const shouldSendInviteDisable = !selectedRole || isLoading || shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid);

  /** Adds a newly invited member to the members store when needed. */
  function addNewMemberToStore(members) {
    if (!members) {
      return;
    }
    const newMemberResponse = members.find(allMembers => !projectMembers.some(oldMembers => oldMembers.identity === allMembers.identity));
    if (!newMemberResponse || FaroCoreApiUtils.Member.isTeam(newMemberResponse)) {
      return;
    }
    const isAlreadyExistsInWorkspace = companyMemberIdentities.has(newMemberResponse.identity);
    if (!isAlreadyExistsInWorkspace) {
      /**
       * The new member should be added to the member store as well.
       * We should use the setOne because we are updating the company role for all members
       * that are present in this list for a Project Role and we should have a Company Role.
       * If we update the role to project in the members store, we will not be able to remove
       * these members from the member page. The user needs to reload the page to have the company
       * role again.
       */
      dispatch(setOne(convertToMemberTypes(newMemberResponse)));
    }
  }

  /** Submit members to be invited */
  async function handleConfirm() {
    if (isLoading || !selectedRole || !uniqueSelectedValues.length) {
      return;
    }
    setIsLoading(true);
    trackEvent({
      name: ProjectEvents.inviteMember,
      props: {
        location,
        numberOfMembers: uniqueSelectedValues.length,
        role: selectedRole
      }
    });
    const payload = {
      assignments: [{
        role: selectedRole,
        identities: uniqueSelectedValues
      }]
    };
    try {
      // Add the projectId to processing projects to start the loading spinner in the project
      dispatch(addProcessingProjects([project.id]));
      const response = await faroCoreApiClient.projects.addMembersToProject({
        companyId,
        projectId: project.id,
        payload
      });

      // Add invited member to the store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        dispatch(setOneProjects({
          ...project,
          members: response.data.members
        }));
        addNewMemberToStore(response.data.members);
      }

      // Remove projectId from processing project to stop the loading spinner in the project
      dispatch(removeProcessingProjects([project.id]));
      respondToUser(response, uniqueSelectedValues.length);
      await onInviteSuccess?.();
      reset();
      setPickedRole(initialRole);
    } catch (error) {
      handleErrorWithToast({
        id: `inviteMember-${Date.now().toString()}`,
        title: "Cannot invite the member",
        error
      });
    } finally {
      setIsLoading(false);
    }
  }
  return {
    autocompleteOptions,
    selectedMembers,
    helperText,
    handleMemberSelect,
    onInputChange,
    isValidInput: value => isNewEmailValid(value) || isTeamName(value),
    selectedRole,
    setSelectedRole: setPickedRole,
    canAssignProjectRoles,
    isLoading,
    shouldSendInviteDisable,
    handleConfirm,
    reset
  };
}